import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Download, Share2, Trash2, X } from 'lucide-react';
import { recordingsAPI } from '../../../../api/client';
import { apiError } from '../../../../lib/host';
import { prettyNumber } from '../../CallDialog';
import { formatDuration, when } from '../../calls/callModel';
import AudioPlayer from '../AudioPlayer';
import { formatBytes, initials, RECORDING_STATUS } from '../recordingsModel';

const TABS = ['Call Information', 'Transcript', 'Notes', 'Tags'];
const FILE_STATUS = { stored: 'Stored securely (private)', removed: 'Removed from storage', missing: 'Not stored' };

function Row({ label, children }) {
  return <div className="rc-row"><span>{label}</span><b>{children}</b></div>;
}

/** The selected recording: player, call information, notes, tags, and Download / Delete for those allowed. */
export default function RecordingDetails({ recordingId, onClose, onChanged, onDownload, onDelete, notify }) {
  const [rec, setRec] = useState(null);
  const [tab, setTab] = useState(TABS[0]);
  const [notes, setNotes] = useState('');
  const [tagText, setTagText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(() => recordingsAPI.get(recordingId).then((res) => {
    setRec(res.data); setNotes(res.data.notes || ''); setError('');
  }).catch((err) => { setRec(false); setError(apiError(err, 'Could not load the recording')); }), [recordingId]);
  useEffect(() => { setRec(null); load(); }, [load]);

  if (rec === false) return <aside className="panel cl-details rc-details"><p className="cell-muted">{error}</p></aside>;
  if (!rec) return <aside className="panel cl-details rc-details"><p className="cell-muted">Loading…</p></aside>;
  const [statusLabel, tone] = RECORDING_STATUS[rec.status] || [rec.status, 'grey'];
  const inbound = rec.direction === 'inbound';

  const save = async (data, done) => {
    setBusy(true);
    try {
      const res = await recordingsAPI.update(rec.id, data);
      setRec(res.data);
      notify(done);
      onChanged();
    } catch (err) { notify(apiError(err, 'Could not save')); } finally { setBusy(false); }
  };
  const addTag = (e) => {
    e.preventDefault();
    const t = tagText.trim().toLowerCase();
    if (!t || rec.tags.includes(t)) { setTagText(''); return; }
    save({ tags: [...rec.tags, t] }, `Tag "${t}" added.`).then(() => setTagText(''));
  };

  return (
    <aside className="panel cl-details rc-details" aria-label="Recording details">
      <div className="cl-details-head"><b>Recording Details</b>
        <button type="button" className="rc-icon" aria-label="Close details" onClick={onClose}><X size={16} /></button></div>
      <div className="rc-identity">
        <span className="rc-avatar">{initials(rec)}</span>
        <div><b>{rec.contact_name || prettyNumber(rec.number)}</b><small>{prettyNumber(rec.number)}</small></div>
        <div className="rc-identity-side"><span className={`rc-type ${rec.direction}`}>{rec.kind === 'voicemail' ? 'Voicemail' : inbound ? 'Incoming' : 'Outgoing'}</span>
          <small>{when(rec.started_at)}</small></div>
      </div>

      {rec.playable && rec.can.play
        ? <AudioPlayer key={rec.id} recordingId={rec.id} duration={rec.duration_seconds} label={`Recording of the call with ${rec.contact_name || rec.number}`} />
        : !rec.can.play ? <p className="rc-unavailable grey">Your role can see this recording but not play it.</p>
        : <p className={`rc-unavailable ${tone}`}>This recording can't be played: <b>{statusLabel}</b>{rec.status === 'processing' ? ' (it will be ready in a few minutes)' : ''}{rec.last_error ? ` · ${rec.last_error}` : ''}.</p>}

      <div className="cl-tabs" role="tablist">
        {TABS.map((t) => <button type="button" role="tab" key={t} aria-selected={tab === t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>{t}</button>)}
      </div>

      {tab === 'Call Information' && (
        <div className="rc-info">
          <Row label="Call ID"><span>#{rec.call_id}</span>
            <button type="button" className="rc-icon small" aria-label="Copy call ID" onClick={() => navigator.clipboard?.writeText(String(rec.call_id)).then(() => notify('Call ID copied.'))}><Copy size={12} /></button></Row>
          <Row label="From">{prettyNumber(rec.from_number)}</Row>
          <Row label="To">{prettyNumber(rec.to_number)}</Row>
          <Row label="Call Type">{rec.kind === 'voicemail' ? 'Voicemail' : inbound ? 'Incoming' : 'Outgoing'}</Row>
          <Row label="Start Time">{when(rec.answered_at || rec.started_at)}</Row>
          <Row label="End Time">{when(rec.ended_at) || '—'}</Row>
          <Row label="Duration">{formatDuration(rec.duration_seconds)}</Row>
          <Row label="Agent">{rec.agent?.name || '—'}</Row>
          <Row label="Recording Status"><span className={`rc-status ${tone}`}>{statusLabel}</span></Row>
          <Row label="Recording File">{FILE_STATUS[rec.file_status]}{rec.size_bytes ? ` · ${formatBytes(rec.size_bytes)}` : ''}</Row>
          <Row label="Retention">{rec.retention_until ? `Deleted automatically on ${when(rec.retention_until)?.split(',')[0]}` : 'Kept until deleted'}</Row>
        </div>
      )}
      {tab === 'Transcript' && <p className="cell-muted rc-pad">Transcripts aren't available yet: calls are recorded, not transcribed.</p>}
      {tab === 'Notes' && (
        <form className="rc-pad" onSubmit={(e) => { e.preventDefault(); save({ notes }, 'Notes saved.'); }}>
          <textarea className="rc-notes" rows={5} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!rec.can.annotate}
            placeholder={rec.can.annotate ? 'What was this call about? Follow-ups, outcome…' : 'No notes.'} aria-label="Notes" />
          {rec.can.annotate && <button type="submit" className="primary-action" disabled={busy || notes === (rec.notes || '')}>Save notes</button>}
        </form>
      )}
      {tab === 'Tags' && (
        <div className="rc-pad">
          <div className="rc-tags">
            {rec.tags.length === 0 && <span className="cell-muted">No tags yet.</span>}
            {rec.tags.map((t) => (
              <span key={t} className="rc-tag">{t}
                {rec.can.annotate && <button type="button" aria-label={`Remove tag ${t}`} disabled={busy} onClick={() => save({ tags: rec.tags.filter((x) => x !== t) }, `Tag "${t}" removed.`)}><X size={11} /></button>}
              </span>
            ))}
          </div>
          {rec.can.annotate && rec.tags.length < 10 && (
            <form className="rc-tag-form" onSubmit={addTag}>
              <input value={tagText} maxLength={30} onChange={(e) => setTagText(e.target.value)} placeholder="Add a tag, e.g. sales" aria-label="New tag" />
              <button type="submit" className="secondary-action" disabled={busy || !tagText.trim()}>Add</button>
            </form>
          )}
        </div>
      )}

      <div className="rc-detail-actions">
        {rec.can.download && <button type="button" className="primary-action" disabled={!rec.playable} onClick={() => onDownload(rec)}><Download size={14} />Download</button>}
        <button type="button" className="secondary-action" disabled title="Sharing recordings with team members isn't available yet"><Share2 size={14} />Share (Internal)</button>
        {rec.can.delete && <button type="button" className="secondary-action danger" onClick={() => onDelete(rec)}><Trash2 size={14} />Delete</button>}
      </div>
    </aside>
  );
}
